import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { GraduationCap, Building2, Mail, Lock, User } from 'lucide-react';
import { toast } from 'sonner';

const API = `/api`;

const SLPSignup = () => {
  const [formData, setFormData] = useState({
    slp_name: '', email: '', password: '', confirmPassword: '', organization_name: '', district: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (formData.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/slp/signup`, {
        slp_name: formData.slp_name, email: formData.email, password: formData.password,
        organization_name: formData.organization_name, district: formData.district || null
      }, { withCredentials: true });
      toast.success('Account created! Please log in.');
      navigate('/slp/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--fe-bg)' }}>
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-[#2C4C3B] rounded-2xl mb-4">
            <GraduationCap className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-4xl font-semibold text-[#1C1C1C] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Join FrameEd</h1>
          <p className="text-[#595959]">Set up your school's referral system</p>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5"><User className="w-4 h-4 inline mr-2 text-[#595959]" />Your Name (SLP)</label>
              <input type="text" required value={formData.slp_name} onChange={(e) => setFormData({...formData, slp_name: e.target.value})} placeholder="Mary Johnson"
                className="w-full h-11 px-4 rounded-xl border border-[#E5E5E5] bg-[#FAFAF8] text-sm focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B] outline-none transition-all" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5"><Mail className="w-4 h-4 inline mr-2 text-[#595959]" />Email Address</label>
              <input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="mary@school.edu"
                className="w-full h-11 px-4 rounded-xl border border-[#E5E5E5] bg-[#FAFAF8] text-sm focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B] outline-none transition-all" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5"><Lock className="w-4 h-4 inline mr-2 text-[#595959]" />Password</label>
                <input type="password" required value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} placeholder="Min 8 characters"
                  className="w-full h-11 px-4 rounded-xl border border-[#E5E5E5] bg-[#FAFAF8] text-sm focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B] outline-none transition-all" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5">Confirm Password</label>
                <input type="password" required value={formData.confirmPassword} onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})} placeholder="Confirm password"
                  className="w-full h-11 px-4 rounded-xl border border-[#E5E5E5] bg-[#FAFAF8] text-sm focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B] outline-none transition-all" />
              </div>
            </div>
            <div className="border-t border-[#E5E5E5] pt-5">
              <h3 className="text-lg font-semibold text-[#1C1C1C] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>School/Organization Information</h3>
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5"><Building2 className="w-4 h-4 inline mr-2 text-[#595959]" />School Name</label>
                  <input type="text" required value={formData.organization_name} onChange={(e) => setFormData({...formData, organization_name: e.target.value})} placeholder="Lincoln Elementary School"
                    className="w-full h-11 px-4 rounded-xl border border-[#E5E5E5] bg-[#FAFAF8] text-sm focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B] outline-none transition-all" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5">District (Optional)</label>
                  <input type="text" value={formData.district} onChange={(e) => setFormData({...formData, district: e.target.value})} placeholder="Springfield School District"
                    className="w-full h-11 px-4 rounded-xl border border-[#E5E5E5] bg-[#FAFAF8] text-sm focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B] outline-none transition-all" />
                </div>
              </div>
            </div>
            <button type="submit" disabled={loading}
              className="w-full bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-medium rounded-xl px-6 py-3 transition-colors disabled:bg-[#E5E5E5] disabled:text-[#595959] disabled:cursor-default">
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>
          <div className="mt-6 text-center">
            <p className="text-sm text-[#595959]">Already have an account?{' '}<Link to="/slp/login" className="text-[#2C4C3B] font-medium hover:underline">Log in</Link></p>
          </div>
          <div className="mt-4 p-3 bg-[#2C4C3B]/5 rounded-xl text-center">
            <p className="text-xs text-[#595959]"><strong>Free during beta!</strong> Start using the referral tool at your school today.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SLPSignup;
